import { useState, useEffect, useMemo, Fragment } from 'react'
import { StatusIdea, InteractionEvent, INTERACTION_EVENT_OPEN, CuratedFolder } from 'interfaces'
import { FetchCuratedFolderPageContent } from 'components/Fetch'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { GRID_BREAKPOINTS } from 'components/ContentGridView'
import styles from './StatusesBricksView.pcss'
import { StatusIdeaCard } from 'components/Card'
import ScrollListener from 'components/ScrollListener'
import { renderLoadingCards } from 'routes/find/routes/home/components/utils'

export interface ScrollConfiguration {
  element?: Element
  useWindow?: boolean
}

export interface StatusesBricksViewProps {
  folder: CuratedFolder
  items: StatusIdea[]
  seed: string
  startPage?: number
  scroll?: ScrollConfiguration
  trackInteraction?(categoryIds: string[], event: InteractionEvent): Promise<any>
  onItemShared?(folder: CuratedFolder, content: StatusIdea): void
}

export function StatusesBricksView(props: StatusesBricksViewProps) {
  const [page, setPage] = useState(props.startPage || 0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<StatusIdea[]>(props.items)

  useEffect(() => {
    setLoading(true)
    setHasNextPage(true)
  }, [props.seed])

  useEffect(() => {
    if (props.trackInteraction) {
      props.trackInteraction(props.folder.streams.map(s => s.id.toString()), INTERACTION_EVENT_OPEN)
    }
  }, [])

  const onItemShared = (content: StatusIdea) => {
    if (props.onItemShared) {
      props.onItemShared(props.folder, content)
    }
  }

  const scrollElement = useMemo(() => {
    return props.scroll?.element || document.getElementsByTagName('main')[0]
  }, [props.scroll])

  const loadNextPage = () => {
    if (loading || !hasNextPage) {
      return
    }

    setLoading(true)
    setPage(page + 1)
  }

  const onFetched = (data: StatusIdea[]) => {
    setLoading(false)
    setHasNextPage(data.length > 0)
    setItems([...items, ...data])
  }

  const onFetchError = () => {
    setLoading(false)
  }

  return (
    <Fragment>
      <FetchCuratedFolderPageContent
        page={page}
        folder={props.folder}
        statusesSeed={props.seed}
        onFetched={onFetched}
        onError={onFetchError}
      />
      <ScrollListener
        className={styles.bricks}
        disabled={loading}
        emitTreshold={250}
        onScrollDebounceTime={50}
        emitInitial={false}
        useWindow={props.scroll?.useWindow}
        scrollElement={scrollElement}
        onScroll={loadNextPage}
      >
        <ResponsiveMasonry columnsCountBreakPoints={GRID_BREAKPOINTS}>
          <Masonry gutter="12px" className={styles.masonry}>
            {items.map((item) => (
              <StatusIdeaCard
                key={item.id}
                content={item}
                onShared={onItemShared}
              />
            ))}
            {loading && renderLoadingCards()}
          </Masonry>
        </ResponsiveMasonry>
      </ScrollListener>
    </Fragment>
  )
}

export default StatusesBricksView
